import { ReactNode } from "react";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImageIcon, ListChecks } from "lucide-react";

interface StepLayoutProps {
  visual: ReactNode;
  list: ReactNode;
  /** Pixel height for desktop and the panel height inside mobile tabs. */
  height?: number;
  dataCount?: number;
}

/**
 * Responsive split layout for onboarding step content.
 * - Mobile (<lg): tabbed Visual / Data (full width each).
 * - Desktop: resizable horizontal split.
 */
export function StepLayout({ visual, list, height = 500, dataCount }: StepLayoutProps) {
  const mobilePanel = { height: `${height}px` };

  return (
    <>
      {/* Mobile / Tablet */}
      <div className="lg:hidden">
        <Tabs defaultValue="visual" className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="visual" className="gap-2">
              <ImageIcon className="h-4 w-4" /> Visual
            </TabsTrigger>
            <TabsTrigger value="data" className="gap-2">
              <ListChecks className="h-4 w-4" />
              Data {typeof dataCount === "number" ? `(${dataCount})` : ""}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="visual" className="mt-3">
            <div className="rounded-xl border overflow-hidden" style={mobilePanel}>
              {visual}
            </div>
          </TabsContent>
          <TabsContent value="data" className="mt-3">
            <div className="rounded-xl border overflow-hidden" style={mobilePanel}>
              {list}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <ResizablePanelGroup
          direction="horizontal"
          className="rounded-xl border"
          style={{ minHeight: `${height}px` }}
        >
          <ResizablePanel defaultSize={65} minSize={40}>
            {visual}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={35} minSize={25}>
            <div style={{ height: `${height}px` }} className="overflow-hidden">
              {list}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </>
  );
}
